import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Autocomplete,
  Button,
  Checkbox,
  CircularProgress,
  FormControl,
  FormControlLabel,
  FormGroup,
  TextField,
  Typography,
  Switch,
  Stack,
} from '@mui/material';
import { HOST_API } from 'config';
import usePortalId from 'hooks/portal/usePortalId';
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import axiosInstance from 'utils/axios';
import { motion } from 'framer-motion';
import { ExpandMore, Info } from '@mui/icons-material';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import { accordionProps } from './elements';
import DragIndicatorSharpIcon from '@mui/icons-material/DragIndicatorSharp';
import { useSnackbar } from 'notistack';
import PartialPageSpinner from 'components/PartialPageSpinner';
import { FormStateDataType } from 'containers/dashboard/Forms/FormListingType';
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd';
import { dispatch, useSelector } from 'redux/store';
import {
  addProduct,
  changeProductQuantity,
  deleteAllProductsTemporary,
  deleteProducts,
  displayDefinedProducts,
  makeAllproductsOptional,
  makeDefaultProduct,
  makefirstProductDeafult,
  optionalProduct,
  reOrder,
  saveAllTemporaryDeletedProducts,
  setProductValue,
} from 'redux/slices/productFormEditor';
import ShowDropdown from './ShowDropdown';
import { useQueryClient } from '@tanstack/react-query';
import { GET_PORTAL_FORM_API_URL } from 'api/apiConstants';
let tempFlag = false;
let initialRenderFlag = false;
function ProductsFormEdit({
  formId,
  state,
  formData,
  productDropdownInitial,
  setPublishFormData,
  setProductLoading,
  productLoading,
  setFormUpdated,
  publishFormData,
  formUpdated,
  onChangeMode,
  allowCustomProduct,
  selectSingleProduct,
  changeKeyValue,
}: {
  formId: string | undefined;
  state: FormStateDataType;
  formData: any;
  setPublishFormData: React.Dispatch<React.SetStateAction<any>>;
  publishFormData: any;
  setProductLoading: React.Dispatch<React.SetStateAction<boolean>>;
  productLoading: boolean;
  setFormUpdated: React.Dispatch<React.SetStateAction<number>>;
  productDropdownInitial: boolean;
  formUpdated: number;
  allowCustomProduct: boolean;
  onChangeMode: (e: React.ChangeEvent<HTMLInputElement>) => void;
  selectSingleProduct: boolean;
  changeKeyValue: (name: string, value: boolean) => void;
}) {
  const { t } = useTranslation();
  const portalId = usePortalId();
  const { enqueueSnackbar } = useSnackbar();
  const [isLoading, setIsLoading] = useState(false);
  const [options, setOptions] = useState<any>([]);
  const [loadingIndex, setLoadingIndex] = useState(-1);
  const { products } = useSelector((state: any) => state.productsFromEditor);
  const [tempProductOptions, setTempProductOptions] = useState<any>([]);
  const [showProductDropdown, setShowProductDropdown] = useState<boolean>(
    productDropdownInitial
  );
  const [saveTemoraryData, setSaveTemporaryData] = useState<any[]>([]);
  const queryClient = useQueryClient();

  // api to get all products
  useEffect(() => {
    const tempProductArr: any = [];
    setIsLoading(true);
    axiosInstance
      .get(
        `${HOST_API}/formpayhome/product/fetchFormpayProduct?portal_id=${portalId}&sort=${true}`
      )
      .then(resp => {
        resp.data.data.map((product: any, index: number) => {
          tempProductArr.push({
            label: product.name,
            value: product.name,
            price: product.price,
            product_id: product._id.$oid,
            order: index,
            optional_product: false,
            change_quantity: false,
            default_product: false,
          });
        });
        setOptions([...tempProductArr]);
        setIsLoading(false);
      });
  }, []);

  // function to add new product
  const addNewProduct = () => {
    if (products.length !== options.length) {
      dispatch(
        addProduct({
          value: '',
          price: 0,
          product_id: '',
          order: 0,
          optional_product: false,
          change_quantity: false,
          default_product: false,
        })
      );
    } else {
      enqueueSnackbar('No more products to add', {
        variant: 'error',
      });
    }
  };
  const { formDetails } = useSelector(state => state.formEditor);
  const showPreview =
    (publishFormData && formUpdated !== 0) ||
    (state.selectedHubspotFormId && formData.hubspot_form_id !== '',
    formData.hubspot_form_id !== '');
  // function to update products data in form
  const updateProductsInForm = async () => {
    if (tempFlag === true) {
      try {
        setProductLoading(true);
        const response = await axiosInstance.post(
          '/formpayhome/portal/updatePortalFormSettings/' + formDetails.formId,
          {
            products: products,
            portalId: portalId,
            allow_custom_product: allowCustomProduct,
          }
        );
        if (response.data.success) {
          if (showPreview) {
            setPublishFormData(response.data);
          }
        }
      } catch (error: any) {
        enqueueSnackbar(error.message, { variant: 'error' });
      } finally {
        setProductLoading(false);
        setLoadingIndex(-1);
      }
    }
  };

  // function for selecting product
  const handleChange = (index: number, data: any) => {
    if (tempProductOptions[index] !== undefined) {
      const arr = tempProductOptions;
      arr.splice(index, 1, data.value);
      setTempProductOptions(arr);
    } else {
      const tempOptions = [...tempProductOptions, data.value];
      setTempProductOptions(tempOptions);
    }
    setLoadingIndex(index);
    dispatch(
      setProductValue({
        data,
        index,
      })
    );
  };

  useEffect(() => {
    if (products[products.length - 1]?.value !== '') {
      updateProductsInForm();
    }
  }, [products]);

  // function for deleting product
  const deleteProduct = (index: number) => {
    tempProductOptions.splice(index, 1);
    dispatch(
      deleteProducts({
        index,
      })
    );
  };

  // function for allowing to change quantity
  const handleChangeQuantity = (
    event: React.ChangeEvent<HTMLInputElement>,
    index: number
  ) => {
    setLoadingIndex(index);
    dispatch(changeProductQuantity({ index }));
  };

  const makeProductDefault = (
    event: React.ChangeEvent<HTMLInputElement>,
    index: number
  ) => {
    setLoadingIndex(index);
    dispatch(makeDefaultProduct({ index }));
  };

  // function for making the product optional
  const handleOptionalProduct = (
    event: React.ChangeEvent<HTMLInputElement>,
    index: number
  ) => {
    setLoadingIndex(index);
    dispatch(
      optionalProduct({
        index,
      })
    );
  };

  const onDragEnd = (results: any) => {
    dispatch(reOrder({ results }));
  };

  useEffect(() => {
    const temp = formData?.products || [];
    if (formData.products?.length !== 0) {
      dispatch(displayDefinedProducts(temp));
    }
    const arr: any = [];
    if (temp.length > 0) {
      temp.map((item: any) => {
        arr.push(item.value);
      });
    }

    setTempProductOptions(arr);

    setTimeout(() => {
      tempFlag = true;
    }, 4000);
  }, []);

  useEffect(() => {
    queryClient.setQueryData(
      [GET_PORTAL_FORM_API_URL, formData._id],
      (formData: any) => {
        return {
          ...formData,
          data: { ...formData.data, products },
        };
      }
    );
  }, [products, portalId, queryClient]);

  useEffect(() => {
    if (products?.length !== 0) {
      dispatch(makeAllproductsOptional({ showProductDropdown }));
      dispatch(makefirstProductDeafult({ showProductDropdown }));
    }
  }, [showProductDropdown]);

  useEffect(() => {
    // @ts-ignore
    if (publishFormData && window.showPreviewForForm) {
      setFormUpdated(value => value + 1);
    }
  }, [publishFormData, setFormUpdated]);
  useEffect(() => {
    if (initialRenderFlag) {
      const callApiSelectSingleProduct = async () => {
        try {
          const response = await axiosInstance.post(
            '/formpayhome/portal/updatePortalFormSettings/' +
              formDetails.formId,
            {
              portal_id: portalId,
              select_single_product: selectSingleProduct,
            }
          );
          if (response.data.success) {
            if (showPreview) {
              setPublishFormData(response.data);
            }
          }
        } catch (err: any) {
          enqueueSnackbar(err.message, {
            variant: 'error',
          });
        }
      };
      callApiSelectSingleProduct();
    }
  }, [selectSingleProduct]);

  useEffect(() => {
    if (allowCustomProduct && initialRenderFlag) {
      setSaveTemporaryData(products);
      dispatch(deleteAllProductsTemporary([]));
    }
    if (!allowCustomProduct && initialRenderFlag) {
      if (saveTemoraryData.length > 0) {
        dispatch(saveAllTemporaryDeletedProducts(saveTemoraryData));
        setSaveTemporaryData([]);
      } else {
        updateProductsInForm();
      }
    }
    setTimeout(() => {
      initialRenderFlag = true;
    }, 4000);
  }, [allowCustomProduct]);

  return (
    <>
      {
        <Accordion defaultExpanded square={false} sx={accordionProps}>
          <AccordionSummary expandIcon={<ExpandMore />}>
            <Typography
              sx={{ marginBottom: '5px' }}
              variant='subtitle2'
              color='text secondary'
            >
              {t('custom_product_settings')}
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <FormControlLabel
              control={
                <Switch
                  name='allowCustomProduct'
                  checked={allowCustomProduct}
                  onChange={onChangeMode}
                />
              }
              label={t('allow_custom_product')}
            />
            {allowCustomProduct ? (
              <div className='mt-2 flex items-center text-[14px] text-gray-600 ml-1'>
                <Info sx={{ width: '15px', height: '15px' }} />{' '}
                <span className='ml-1'>{t('if_custom_product_is_enable')}</span>
              </div>
            ) : (
              <div className='mt-2 flex items-center text-[14px] text-gray-600 ml-1'>
                <Info sx={{ width: '15px', height: '15px' }} />{' '}
                <span className='ml-1'>{t('custom_product_info_to_sell')}</span>
              </div>
            )}
          </AccordionDetails>
        </Accordion>
      }
      <Accordion defaultExpanded square={false} sx={accordionProps}>
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Typography
            sx={{ marginBottom: '5px' }}
            variant='subtitle2'
            color='text secondary'
          >
            {t('product_display_setting')}
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Stack spacing={3}>
            <ShowDropdown
              setShowProductDropdown={setShowProductDropdown}
              showProductDropdown={showProductDropdown}
              formId={formId}
              setPublishFormData={setPublishFormData}
              formData={formData}
              productDropdownInitial={productDropdownInitial}
              publishFormData={publishFormData}
              formUpdated={formUpdated}
              setFormUpdated={setFormUpdated}
              state={state}
              allowCustomProduct={allowCustomProduct}
            />
            <FormControlLabel
              control={
                <Checkbox
                  name='selectSingleProduct'
                  checked={selectSingleProduct}
                  onChange={onChangeMode}
                />
              }
              label={t('select_single')}
            />
          </Stack>
        </AccordionDetails>
      </Accordion>

      {products.length > 1 ? (
        <>
          <div className='mt-2 flex items-center text-[14px] text-gray-600 ml-1'>
            <Info sx={{ width: '15px', height: '15px' }} />{' '}
            <span className='ml-1'> {t('draggable')} </span>
          </div>
        </>
      ) : (
        <></>
      )}

      <DragDropContext onDragEnd={(results: any) => onDragEnd(results)}>
        <Droppable droppableId='formProductsList'>
          {(provided: any) => (
            <div ref={provided.innerRef} {...provided.droppableProps}>
              {products.length !== 0 ? (
                products.map((item: any, index: number) => {
                  return (
                    <Draggable
                      draggableId={index.toString()}
                      index={index}
                      key={index}
                    >
                      {(provided: any) => (
                        <div
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                          {...provided.dragHandleProps}
                        >
                          <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            key={index}
                            className='mb-8'
                          >
                            <Accordion
                              defaultExpanded
                              square={false}
                              sx={accordionProps}
                            >
                              <AccordionSummary expandIcon={<ExpandMore />}>
                                <Typography
                                  sx={{ marginBottom: '2px' }}
                                  variant='h6'
                                  color='text secondary'
                                  className='flex items-center'
                                >
                                  {products[index].value !== '' ? (
                                    <>
                                      {/* <DragIndicatorSharpIcon fontSize='small' /> */}
                                      {products[index].value}&nbsp;
                                      {loadingIndex === index ? (
                                        <>
                                          <CircularProgress
                                            size={15}
                                            className='ml-2'
                                          />
                                        </>
                                      ) : (
                                        <></>
                                      )}
                                    </>
                                  ) : (
                                    <></>
                                  )}
                                </Typography>
                              </AccordionSummary>
                              <AccordionDetails>
                                <FormControl fullWidth>
                                  <Autocomplete
                                    key={index}
                                    value={products[index]?.value}
                                    onChange={(newValue: any | null, value) => {
                                      handleChange(index, value);
                                    }}
                                    options={options}
                                    getOptionDisabled={(options: any) => {
                                      return tempProductOptions.includes(
                                        options.value
                                      );
                                    }}
                                    id='combo-box-demo'
                                    renderInput={params => (
                                      <TextField {...params} label='Products' />
                                    )}
                                  />
                                  <TextField
                                    className='mt-5'
                                    value={products[index].price}
                                    label='Amount'
                                    disabled
                                  />
                                </FormControl>
                                <FormGroup className='mt-4'>
                                  {showProductDropdown ? (
                                    <FormControlLabel
                                      value='Default Product'
                                      control={
                                        <Checkbox
                                          className='ml-2'
                                          checked={
                                            products[index].default_product
                                          }
                                          onChange={event =>
                                            makeProductDefault(event, index)
                                          }
                                        />
                                      }
                                      label='Default Product'
                                      labelPlacement='end'
                                    />
                                  ) : (
                                    <></>
                                  )}

                                  <FormControlLabel
                                    value='Allow to change Quantity'
                                    control={
                                      <Checkbox
                                        className='ml-2'
                                        checked={
                                          products[index].change_quantity
                                        }
                                        onChange={event =>
                                          handleChangeQuantity(event, index)
                                        }
                                      />
                                    }
                                    label='Allow to Change Quantity'
                                    labelPlacement='end'
                                  />

                                  {products.length === 1 ? (
                                    <>
                                      <FormControlLabel
                                        value='Optional Product'
                                        checked={
                                          products[index].optional_product
                                        }
                                        control={
                                          <Checkbox
                                            className='ml-2'
                                            disabled={!showProductDropdown}
                                            onChange={event =>
                                              handleOptionalProduct(
                                                event,
                                                index
                                              )
                                            }
                                          />
                                        }
                                        label='Optional Product'
                                        labelPlacement='end'
                                      />
                                    </>
                                  ) : (
                                    <>
                                      <FormControlLabel
                                        value='Optional Product'
                                        checked={
                                          products[index].optional_product
                                        }
                                        control={
                                          <Checkbox
                                            className='ml-2'
                                            onChange={event =>
                                              handleOptionalProduct(
                                                event,
                                                index
                                              )
                                            }
                                          />
                                        }
                                        label='Optional Product'
                                        labelPlacement='end'
                                      />
                                    </>
                                  )}
                                </FormGroup>
                                <>
                                  <Button
                                    color='error'
                                    className='mt-6 flex items-center'
                                    onClick={() => {
                                      deleteProduct(index);
                                    }}
                                  >
                                    <DeleteOutlineOutlinedIcon fontSize='medium' />
                                    &nbsp;
                                    {t('delete_product')}
                                  </Button>
                                </>
                              </AccordionDetails>
                            </Accordion>
                          </motion.div>
                        </div>
                      )}
                    </Draggable>
                  );
                })
              ) : (
                <></>
              )}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </DragDropContext>

      {isLoading ? (
        <PartialPageSpinner />
      ) : (
        <>
          <Button
            size='medium'
            className='w-[25%] normal-case'
            onClick={addNewProduct}
            disabled={allowCustomProduct}
          >
            + {t('add_product')}
          </Button>
        </>
      )}
    </>
  );
}

export default ProductsFormEdit;
